import { shimCaptionsLanguagesIfNeeded } from './plyr_embed_caption_options';
import { syncCarouselSlideUrlAfterRefresh } from './lib/carousel_blob_refresh.js';

const rootDataset = document.documentElement.dataset || {};
const SAS_REFRESH_RATIO = parseFloat(rootDataset.sasRefreshRatio || 0.8);
const SAS_EXPIRY_MINUTES = parseInt(rootDataset.sasExpiryMinutes || 30, 10);
const REFRESH_INTERVAL_MS = Math.round(SAS_EXPIRY_MINUTES * SAS_REFRESH_RATIO * 60 * 1000);
const CUSTOM_BLOB_HOST = rootDataset.blobHost;
const FALLBACK_SELECTORS = ['img', 'video', 'audio', 'source', 'a', 'tr[data-resource-row]'];

let refreshIntervalId = null;

// Track media elements currently being updated to prevent duplicate updates
const mediaUpdateInProgress = new WeakSet();

function parseAttributeList(node) {
  const attrs = node.getAttribute('data-blob-attrs');
  if (!attrs) return [];
  return attrs.split(',').map((attr) => attr.trim()).filter(Boolean);
}

function collectTargets() {
  const elements = document.querySelectorAll('[data-blob-signed-id], [data-blob-poster-signed-id]');
  const map = new Map();

  elements.forEach((element) => {
    const signedId = element.getAttribute('data-blob-signed-id');
    if (signedId) {
      const attrs = parseAttributeList(element);
      if (attrs.length > 0) {
        const entry = map.get(signedId) || [];
        entry.push({ element, attrs });
        map.set(signedId, entry);
      }
    }

    const posterSignedId = element.getAttribute('data-blob-poster-signed-id');
    if (posterSignedId) {
      const entry = map.get(posterSignedId) || [];
      entry.push({ element, attrs: ['poster', 'data-poster'] });
      map.set(posterSignedId, entry);
    }
  });

  return map;
}

function collectFallbackTargets() {
  const results = [];
  FALLBACK_SELECTORS.forEach((selector) => {
    document.querySelectorAll(selector).forEach((element) => {
      if (element.dataset.blobSignedId) return;
      const attrCandidates = [];
      if (selector === 'a' && element.hasAttribute('href')) {
        attrCandidates.push('href');
      }
      if (element.hasAttribute('src')) {
        attrCandidates.push('src');
      }
      if (element.hasAttribute('data-href')) {
        attrCandidates.push('data-href');
      }
      if (element.hasAttribute('data-src')) {
        attrCandidates.push('data-src');
      }

      attrCandidates.forEach((attr) => {
        const url = element.getAttribute(attr);
        if (!url || !isResolvableUrl(url)) return;
        results.push({ element, attr, url });
      });
    });
  });
  return results;
}

function getMediaElement(element) {
  if (element.matches('video, audio')) {
    return element;
  }
  if (element.matches('source')) {
    return element.closest('video, audio');
  }
  return null;
}

/**
 * Get the Plyr instance for a media element
 */
function getPlyrInstance(mediaElement) {
  // Check if Plyr instance is stored on the element
  if (mediaElement._plyrInstance) {
    return mediaElement._plyrInstance;
  }
  
  // Check if element is wrapped by Plyr and has plyr property
  const plyrContainer = mediaElement.closest('.plyr');
  if (plyrContainer) {
    // Try to find Plyr instance via the container's plyr property
    if (plyrContainer.plyr) {
      return plyrContainer.plyr;
    }
    // Some versions store it differently
    if (window.Plyr && plyrContainer.plyr instanceof window.Plyr) {
      return plyrContainer.plyr;
    }
  }
  
  return null;
}

/**
 * Capture current playback state from Plyr or native element
 */
function captureState(mediaElement, plyrInstance) {
  if (plyrInstance) {
    return {
      currentTime: plyrInstance.currentTime || 0,
      paused: plyrInstance.paused,
      volume: plyrInstance.volume,
      muted: plyrInstance.muted,
      playbackRate: plyrInstance.speed || 1,
      wasPlaying: plyrInstance.playing || false
    };
  }
  
  return {
    currentTime: mediaElement.currentTime || 0,
    paused: mediaElement.paused,
    volume: mediaElement.volume,
    muted: mediaElement.muted,
    playbackRate: mediaElement.playbackRate || 1,
    wasPlaying: !mediaElement.paused && !mediaElement.ended
  };
}

/**
 * Restore playback state to Plyr instance
 */
function restoreStatePlyr(plyrInstance, state) {
  return new Promise((resolve) => {
    const doRestore = () => {
      try {
        // Restore playback position first
        if (state.currentTime > 0) {
          plyrInstance.currentTime = state.currentTime;
        }
        
        // Restore volume settings
        plyrInstance.volume = state.volume;
        plyrInstance.muted = state.muted;
        
        // Restore playback rate if available
        if (typeof plyrInstance.speed !== 'undefined') {
          plyrInstance.speed = state.playbackRate;
        }
        
        // Resume playback if was playing
        if (state.wasPlaying) {
          const playPromise = plyrInstance.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(e => {
              console.debug('[BlobRefresh] Auto-play blocked:', e.message);
            });
          }
        }
        
        console.debug('[BlobRefresh] State restored:', {
          currentTime: state.currentTime,
          wasPlaying: state.wasPlaying
        });
      } catch (e) {
        console.error('[BlobRefresh] Error restoring Plyr state:', e);
      }
      resolve();
    };
    
    // Wait for loadeddata event before restoring
    const onLoaded = () => {
      plyrInstance.off('loadeddata', onLoaded);
      // Small delay to ensure Plyr has fully processed the source change
      setTimeout(doRestore, 50);
    };
    
    plyrInstance.on('loadeddata', onLoaded);
    
    // Fallback timeout in case loadeddata doesn't fire
    setTimeout(() => {
      plyrInstance.off('loadeddata', onLoaded);
      doRestore();
    }, 5000);
  });
}

/**
 * Get current poster URL from a video element or Plyr's poster element (so we can preserve it when updating source)
 */
function getCurrentPosterUrl(mediaElement) {
  if (!mediaElement || mediaElement.tagName.toLowerCase() !== 'video') return null;
  const fromVideo = mediaElement.getAttribute('poster') || mediaElement.getAttribute('data-poster');
  if (fromVideo) return fromVideo;
  const wrapper = mediaElement.closest('.plyr');
  const posterDiv = wrapper?.querySelector('.plyr__poster');
  if (!posterDiv || !posterDiv.style.backgroundImage) return null;
  const match = posterDiv.style.backgroundImage.match(/url\(["']?([^"')]+)["']?\)/);
  return match ? match[1].trim() : null;
}

/**
 * Update source using Plyr's native API
 * Preserves poster so the thumbnail is not lost when refreshing signed URLs.
 */
async function updatePlyrSource(plyrInstance, mediaElement, sourceElement, newUrl, contentType) {
  if (mediaUpdateInProgress.has(mediaElement)) {
    console.debug('[BlobRefresh] Update already in progress, skipping');
    return;
  }
  
  mediaUpdateInProgress.add(mediaElement);
  
  const isVideo = mediaElement.tagName.toLowerCase() === 'video';
  const posterUrl = getCurrentPosterUrl(mediaElement);
  
  // Capture current state BEFORE changing source
  const state = captureState(mediaElement, plyrInstance);
  
  console.debug('[BlobRefresh] Updating via Plyr API:', {
    currentTime: state.currentTime,
    wasPlaying: state.wasPlaying,
    hasPoster: !!posterUrl
  });
  
  try {
    // Use Plyr's source setter - include poster so it is not lost when Plyr rebuilds the video
    const sourceConfig = {
      type: isVideo ? 'video' : 'audio',
      sources: [{
        src: newUrl,
        type: contentType || (isVideo ? 'video/mp4' : 'audio/mpeg')
      }]
    };
    if (isVideo && posterUrl) {
      sourceConfig.poster = posterUrl;
    }
    plyrInstance.source = sourceConfig;

    // Plyr soft-destroys + rebuilds on source assignment, wiping `captions.languages`.
    // The prototype `toggleCaptions` patch survives, but we still need the array shim.
    const reshim = () => shimCaptionsLanguagesIfNeeded(plyrInstance);
    try { plyrInstance.once('ready', reshim); } catch { /* ignore */ }
    queueMicrotask(reshim);
    setTimeout(reshim, 0);
    setTimeout(reshim, 50);
    setTimeout(reshim, 200);

    // Ensure poster is applied (Plyr may not always apply it from source in some versions)
    if (isVideo && posterUrl && typeof plyrInstance.poster !== 'undefined') {
      plyrInstance.poster = posterUrl;
    }
    
    // Update the source element's data attributes for future refresh tracking
    if (sourceElement) {
      sourceElement.dataset.src = newUrl;
      // Note: Don't update the actual src attribute as Plyr manages it now
    }
    
    // Restore state
    await restoreStatePlyr(plyrInstance, state);
    
  } catch (e) {
    console.error('[BlobRefresh] Error updating Plyr source:', e);
  } finally {
    mediaUpdateInProgress.delete(mediaElement);
  }
}

/**
 * Update source for native HTML5 media (no Plyr)
 */
function updateNativeSource(sourceElement, mediaElement, newUrl) {
  if (mediaUpdateInProgress.has(mediaElement)) {
    console.debug('[BlobRefresh] Update already in progress, skipping');
    return;
  }
  
  mediaUpdateInProgress.add(mediaElement);
  
  // Capture state
  const state = captureState(mediaElement, null);
  
  console.debug('[BlobRefresh] Updating native source:', {
    currentTime: state.currentTime,
    wasPlaying: state.wasPlaying
  });
  
  // Update the source
  sourceElement.setAttribute('src', newUrl);
  sourceElement.dataset.src = newUrl;
  
  // Reload
  mediaElement.load();
  
  // Restore state when ready
  const restore = () => {
    try {
      mediaElement.volume = state.volume;
      mediaElement.muted = state.muted;
      mediaElement.playbackRate = state.playbackRate;
      
      if (state.currentTime > 0 && isFinite(mediaElement.duration)) {
        mediaElement.currentTime = Math.min(state.currentTime, mediaElement.duration - 0.1);
      }
      
      if (state.wasPlaying) {
        mediaElement.play().catch(e => {
          console.debug('[BlobRefresh] Play blocked:', e.message);
        });
      }
    } catch (e) {
      console.error('[BlobRefresh] Restore error:', e);
    } finally {
      mediaUpdateInProgress.delete(mediaElement);
    }
  };
  
  if (mediaElement.readyState >= 1) {
    restore();
  } else {
    mediaElement.addEventListener('loadedmetadata', restore, { once: true });
    // Fallback timeout
    setTimeout(() => {
      if (mediaUpdateInProgress.has(mediaElement)) {
        restore();
      }
    }, 5000);
  }
}

/**
 * Main function to update media source - applies immediately, no deferring
 */
function updateMediaSource(sourceElement, mediaElement, newUrl) {
  const currentSrc = sourceElement.getAttribute('src');
  
  // Skip if URL unchanged
  if (currentSrc === newUrl) {
    return;
  }
  
  const contentType = sourceElement.getAttribute('type');
  const plyrInstance = getPlyrInstance(mediaElement);
  
  console.debug('[BlobRefresh] Updating media source:', {
    hasPlyr: !!plyrInstance,
    contentType
  });
  
  // Always apply immediately - don't defer
  if (plyrInstance) {
    updatePlyrSource(plyrInstance, mediaElement, sourceElement, newUrl, contentType);
  } else {
    updateNativeSource(sourceElement, mediaElement, newUrl);
  }
}

function updateAttributes(element, attrs, url, signedId = null) {
  const mediaElement = getMediaElement(element);
  const isMediaSourceUpdate = mediaElement && attrs.includes('src');
  
  if (isMediaSourceUpdate) {
    updateMediaSource(element, mediaElement, url);
    syncCarouselSlideUrlAfterRefresh(element, 'src', url, signedId);
    return;
  }
  
  // Standard attribute update for non-media elements
  attrs.forEach((attr) => {
    element.setAttribute(attr, url);
    if (attr === 'href' || attr === 'data-href') {
      element.dataset.href = url;
    }
    if (attr === 'src' || attr === 'data-src') {
      element.dataset.src = url;
    }
    if (attr === 'poster' || attr === 'data-poster') {
      element.dataset.poster = url;
    }
  });

  attrs.forEach((attr) => {
    if (attr === 'src' || attr === 'data-src' || attr === 'poster' || attr === 'data-poster') {
      syncCarouselSlideUrlAfterRefresh(element, attr, url, signedId);
    }
  });
}

async function refreshSignedId(signedId, entries) {
  try {
    const response = await fetch(`/signed_blob_urls/${encodeURIComponent(signedId)}`, {
      headers: { 'Accept': 'application/json' },
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const data = await response.json();
    if (!data.url) return;

    const mediaGroups = new Map();
    const nonMediaEntries = [];
    
    entries.forEach(({ element, attrs }) => {
      const mediaElement = getMediaElement(element);
      if (mediaElement && attrs.includes('src')) {
        if (!mediaGroups.has(mediaElement)) {
          mediaGroups.set(mediaElement, []);
        }
        mediaGroups.get(mediaElement).push({ element, attrs });
      } else {
        nonMediaEntries.push({ element, attrs });
      }
    });
    
    // Update non-media elements
    nonMediaEntries.forEach(({ element, attrs }) => {
      updateAttributes(element, attrs, data.url, signedId);
    });
    
    // Update media elements
    mediaGroups.forEach((groupEntries) => {
      const firstEntry = groupEntries[0];
      updateAttributes(firstEntry.element, firstEntry.attrs, data.url, signedId);
    });
  } catch (error) {
    console.error('Failed to refresh signed URL', signedId, error);
  }
}

function groupFallbackTargets(entries) {
  const map = new Map();
  entries.forEach((entry) => {
    const key = entry.url + '|' + entry.attr;
    const list = map.get(key) || [];
    list.push(entry);
    map.set(key, list);
  });
  return map;
}

function isResolvableUrl(url) {
  if (!url) return false;
  if (url.includes('/rails/active_storage/')) return true;
  if (url.includes('blob.core.windows.net')) return true;
  if (CUSTOM_BLOB_HOST && url.includes(CUSTOM_BLOB_HOST)) return true;
  return false;
}

async function refreshByUrl(url, attr, entries) {
  try {
    const response = await fetch(`/signed_blob_urls?url=${encodeURIComponent(url)}`, {
      headers: { 'Accept': 'application/json' },
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const data = await response.json();
    if (!data.url) return;

    const mediaGroups = new Map();
    const nonMediaEntries = [];
    
    entries.forEach(({ element }) => {
      const mediaElement = getMediaElement(element);
      if (mediaElement && attr === 'src') {
        if (!mediaGroups.has(mediaElement)) {
          mediaGroups.set(mediaElement, []);
        }
        mediaGroups.get(mediaElement).push({ element });
      } else {
        nonMediaEntries.push({ element });
      }
    });
    
    // Update non-media elements
    nonMediaEntries.forEach(({ element }) => {
      updateAttributes(element, [attr], data.url, data.signed_id);
      element.dataset.blobSignedId = data.signed_id;
      element.dataset.blobAttrs = attr;
      element.dataset.blobDisposition = attr === 'href' ? 'attachment' : 'inline';
    });
    
    // Update media elements
    mediaGroups.forEach((groupEntries) => {
      const firstEntry = groupEntries[0];
      updateAttributes(firstEntry.element, [attr], data.url, data.signed_id);

      groupEntries.forEach(({ element }) => {
        element.dataset.blobSignedId = data.signed_id;
        element.dataset.blobAttrs = attr;
        element.dataset.blobDisposition = 'inline';
      });
    });
  } catch (error) {
    console.error('Failed to refresh URL', url, error);
  }
}

async function refreshAllSignedUrls() {
  const targets = collectTargets();
  const tasks = [];

  targets.forEach((entries, signedId) => {
    tasks.push(refreshSignedId(signedId, entries));
  });

  const fallbackTargets = groupFallbackTargets(collectFallbackTargets());
  fallbackTargets.forEach((entries, key) => {
    const [url, attr] = key.split('|');
    tasks.push(refreshByUrl(url, attr, entries));
  });

  await Promise.all(tasks);
}

function startRefreshTimer() {
  stopRefreshTimer();
  refreshAllSignedUrls();
  refreshIntervalId = window.setInterval(refreshAllSignedUrls, REFRESH_INTERVAL_MS);
}

function stopRefreshTimer() {
  if (refreshIntervalId) {
    window.clearInterval(refreshIntervalId);
    refreshIntervalId = null;
  }
}

document.addEventListener('turbo:load', startRefreshTimer);
document.addEventListener('turbo:before-cache', stopRefreshTimer);
